/** Форматирование для экрана позиций объёма РД: количество, доверие, решение
 *  HITL, источник позиции и пояснение к числу из `raw`. */

export function fmtQty(q: number | null): string {
  if (q === null || q === undefined) return '—';
  return q.toLocaleString('ru-RU', { maximumFractionDigits: 3 });
}

export function fmtConf(c: number | null): string {
  if (c === null || c === undefined) return '—';
  return `${Math.round(c * 100)}%`;
}

export function decisionLabel(d: string | null): string {
  switch (d) {
    case 'accepted':
      return 'принято';
    case 'rejected':
      return 'отклонено';
    case 'disputed':
      return 'спорно';
    default:
      return '';
  }
}

export type Raw = Record<string, unknown>;

/** `raw` позиции (или его вложенный ключ) как объект; иначе null. */
export function asRaw(v: unknown): Raw | null {
  return v !== null && typeof v === 'object' && !Array.isArray(v) ? (v as Raw) : null;
}

function rowPage(v: unknown): string {
  const r = asRaw(v);
  return r && typeof r.page === 'number' ? `стр. ${r.page}` : 'другая строка';
}

/** Пояснение к числу позиции из `raw`: кратность «на 1 …» по явному числу
 *  документа, «на 1 …» с итогом в другой строке, повтор (вне суммы), причины входного контроля. */
export function rawHint(raw: unknown): string | null {
  const r = asRaw(raw);
  if (!r) return null;
  const reviewReasons = Array.isArray(r.review)
    ? r.review.filter((x): x is string => typeof x === 'string' && x.trim().length > 0)
    : [];
  const reviewSuffix = reviewReasons.length > 0 ? ` [замечания: ${reviewReasons.join(', ')}]` : '';
  const m = asRaw(r.multiplier);
  if (m && typeof m.count === 'number') {
    const by = asRaw(m.by);
    const from =
      by?.kind === 'composition_head'
        ? `голова «${String(by.name ?? '').trim()}»`
        : `по строке ${rowPage(by?.row)}`;
    return `× ${fmtQty(m.count)} (${from}); на 1 — ${fmtQty(
      typeof m.unit_qty === 'number' ? m.unit_qty : null,
    )}${reviewSuffix}`;
  }
  const covered = asRaw(r.per_unit_covered);
  if (covered) {
    const by = Array.isArray(covered.by) ? covered.by[0] : null;
    return covered.kind === 'assembly_parent'
      ? `на 1 изделие — масса в строке родителя (${rowPage(by)}), вне суммы${reviewSuffix}`
      : `на 1 … — итог отдельной строкой (${rowPage(by)}), вне суммы${reviewSuffix}`;
  }
  const pu = asRaw(r.per_unit);
  if (pu) {
    const what = typeof pu.element === 'string' && pu.element ? `на 1 ${pu.element}` : pu.note;
    return `${String(what ?? 'на 1 элемент')} — нужна кратность, вне суммы${reviewSuffix}`;
  }
  const head = asRaw(r.composition_counted);
  if (head) {
    return `голова состава — изделия узла посчитаны строками состава, вне суммы${reviewSuffix}`;
  }
  const other = asRaw(r.other_set_of);
  if (other && typeof other.status === 'string') {
    const doc = typeof other.doc === 'string' ? other.doc.split('.').pop() : 'другом комплекте';
    const row = Array.isArray(other.rows) ? other.rows[0] : null;
    return other.status === 'covered' || other.status === 'covered_sum'
      ? `учтено в ${doc} (${rowPage(row)}), вне суммы${reviewSuffix}`
      : `учтено в другом комплекте — не подтверждено (${OTHER_SET_STATUS[other.status] ?? other.status})${reviewSuffix}`;
  }
  if (r.exclude === 'sheet_total' || r.exclude === 'row_part') {
    return r.exclude === 'sheet_total'
      ? `итог строк таблицы листа — строки в сумме, эта вне суммы${reviewSuffix}`
      : `часть строки таблицы листа — строка в сумме, эта вне суммы${reviewSuffix}`;
  }
  const dup = asRaw(r.dup_of) ?? asRaw(r.dup_candidate);
  if (dup && dup.kind !== 'part_of_total') {
    return `повтор строки ${rowPage(dup)}, вне суммы${reviewSuffix}`;
  }
  const vision = asRaw(r.vision);
  if (vision && typeof vision.verdict === 'string') {
    const read = [vision.qty, vision.unit].filter((v) => typeof v === 'string' && v).join(' ');
    return `по изображению: ${read || '—'} (${VISION_VERDICT[vision.verdict] ?? vision.verdict})${reviewSuffix}`;
  }
  if (reviewReasons.length > 0) {
    return `Входной контроль РД: ${reviewReasons.join(', ')}`;
  }
  return null;
}

/** Статусы сверки «учтено в другом комплекте» (`raw.other_set_of.status`). */
const OTHER_SET_STATUS: Record<string, string> = {
  qty_differs: 'в комплекте другое количество',
  unit_differs: 'в комплекте другая единица',
  ambiguous: 'несколько подходящих строк',
  not_found: 'позиция в комплекте не найдена',
  set_absent: 'комплекта нет в объекте',
};

/** Вердикт второго прочтения строки скана по изображению (`raw.vision`). */
const VISION_VERDICT: Record<string, string> = {
  same: 'совпало с OCR',
  differs: 'не совпало с OCR',
  illegible: 'неразборчиво',
  flagged: 'на 1 … или итог',
};

/** Источник позиции: 'rd' — из текста РД (M2.1), 'rd_table' — из таблицы
 *  спецификации/ВОР на чертеже (M2.2 табличный путь). */
export function sourceLabel(role: string): string {
  switch (role) {
    case 'rd_table':
      return 'таблица';
    case 'rd_geometry':
      return 'геометрия';
    case 'rd':
      return 'текст';
    case 'rd_change':
      return 'документ изменения';
    default:
      return role;
  }
}
